import {
  ReactElement,
  ReactNode,
  cloneElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { useMouseStore } from '../../hooks/stores/mouseStore';

export const Dropdown = ({
  id,
  trigger = <></>,
  children,

  onOpen,
  isOpen,
  setOpen,

  style,
  contentStyle,
  horizontalAlign = 'left',
  closeOnClick = true,
  toggleOnTriggerClick = true,
  ignoreTriggerClick = false,
}: {
  id?: string;
  trigger?: ReactNode;
  children?: ReactNode;

  onOpen?: (open: boolean) => void;
  isOpen?: boolean;
  setOpen?: (open: boolean) => void;

  style?: React.CSSProperties;
  contentStyle?: React.CSSProperties;
  horizontalAlign?: 'left' | 'right' | 'center';
  closeOnClick?: boolean;
  toggleOnTriggerClick?: boolean;
  ignoreTriggerClick?: boolean;
}) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentTranslation, setContentTranslation] = useState({
    x: 0,
    y: 0,
  });

  const keepContentInWindowBoundary = () => {
    const paddingX = 10;
    const paddingY = 30;
    if (contentRef.current) {
      const contentRect = contentRef.current.getBoundingClientRect();
      const windowWidth = window.innerWidth;
      const windowHeight = window.innerHeight;

      const translationX =
        contentRect.x + contentRect.width > windowWidth - paddingX
          ? -(contentRect.x + contentRect.width - windowWidth + paddingX)
          : 0;
      const translationY =
        contentRect.y + contentRect.height > windowHeight - paddingY
          ? -(contentRect.y + contentRect.height - windowHeight + paddingY)
          : 0;

      setContentTranslation({
        x: translationX,
        y: translationY,
      });
    }
  };

  useEffect(() => {
    if (onOpen) {
      onOpen(isOpen === undefined ? isOpenInternal : isOpen);
    }

    if (isOpen || isOpenInternal) keepContentInWindowBoundary();
    else setContentTranslation({ x: 0, y: 0 });
  }, [isOpenInternal, isOpen]);

  return (
    <Container
      id={id}
      style={style}
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
    >
      {cloneElement(trigger as ReactElement, {
        onClick: () => {
          if (ignoreTriggerClick) return;

          isOpen === undefined
            ? setIsOpenInternal((prev) => (toggleOnTriggerClick ? !prev : true))
            : setOpen?.(toggleOnTriggerClick ? !isOpen : true);
        },
      })}
      <ContentContainer
        ref={contentRef}
        style={{
          ...contentStyle,
          translate: `${contentTranslation.x}px calc(100% + ${contentTranslation.y}px)`,
        }}
        onClick={(e) => {
          if (!closeOnClick) return;
          isOpen === undefined ? setIsOpenInternal(false) : setOpen?.(false);
        }}
        $isOpen={isOpen === undefined ? isOpenInternal : isOpen}
        $horizontalAlign={horizontalAlign}
      >
        {children}
      </ContentContainer>

      <CloseHook
        isMouseOver={isMouseOver}
        closeMenu={() =>
          isOpen === undefined ? setIsOpenInternal(false) : setOpen?.(false)
        }
      />
    </Container>
  );
};

const CloseHook = ({
  isMouseOver,
  closeMenu,
}: {
  isMouseOver: boolean;
  closeMenu: () => void;
}) => {
  const [mouseData] = useMouseStore();
  useEffect(() => {
    if (mouseData.isDown && !isMouseOver) {
      closeMenu();
    }
  }, [mouseData.isDown]);

  return <></>;
};

const Container = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
`;

const ContentContainer = styled.div<{
  $isOpen: boolean;
  $horizontalAlign: 'left' | 'right' | 'center';
}>`
  z-index: 2;
  position: absolute;
  ${({ $horizontalAlign }) =>
    $horizontalAlign === 'left' ? 'left: 0;' : 'right: 0;'}
  ${({ $horizontalAlign }) =>
    $horizontalAlign === 'center' && 'left: initial; right: initial;'}
  bottom: 0;
  translate: 0 100%;

  opacity: 0;
  pointer-events: none;
  transition:
    opacity var(--transition-2),
    translate var(--transition-1);
  ${({ $isOpen }) => $isOpen && 'opacity: 1; pointer-events: all;'}
`;
